import React from 'react';
import styled from 'styled-components';
import type { LucideIcon } from 'lucide-react';
import { AlertCircle, Calculator } from 'lucide-react';

export type StructuredTone =
  | 'abs'
  | 'rmbs'
  | 'cmbs'
  | 'clo'
  | 'msr'
  | 'reEquity'
  | 'cml'
  | 'rml'
  | 'pe'
  | 'portco'
  | 'equity'
  | 'repo'
  | 'cash'
  | 'multi';

export interface StructuredMetric {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}

export interface StructuredContextRow {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}

const tones: Record<StructuredTone, {
  primary: string;
  secondary: string;
  soft: string;
  border: string;
  glow: string;
}> = {
  abs: {
    primary: '#14b8a6',
    secondary: '#5eead4',
    soft: 'rgba(20, 184, 166, 0.12)',
    border: 'rgba(20, 184, 166, 0.32)',
    glow: 'rgba(13, 148, 136, 0.2)',
  },
  rmbs: {
    primary: '#38bdf8',
    secondary: '#7dd3fc',
    soft: 'rgba(56, 189, 248, 0.12)',
    border: 'rgba(56, 189, 248, 0.32)',
    glow: 'rgba(14, 165, 233, 0.2)',
  },
  cmbs: {
    primary: '#f59e0b',
    secondary: '#fbbf24',
    soft: 'rgba(245, 158, 11, 0.12)',
    border: 'rgba(245, 158, 11, 0.32)',
    glow: 'rgba(217, 119, 6, 0.2)',
  },
  clo: {
    primary: '#34d399',
    secondary: '#6ee7b7',
    soft: 'rgba(52, 211, 153, 0.12)',
    border: 'rgba(52, 211, 153, 0.32)',
    glow: 'rgba(16, 185, 129, 0.2)',
  },
  msr: {
    primary: '#fb7185',
    secondary: '#fda4af',
    soft: 'rgba(251, 113, 133, 0.12)',
    border: 'rgba(251, 113, 133, 0.32)',
    glow: 'rgba(244, 63, 94, 0.18)',
  },
  reEquity: {
    primary: '#22c55e',
    secondary: '#86efac',
    soft: 'rgba(34, 197, 94, 0.12)',
    border: 'rgba(34, 197, 94, 0.32)',
    glow: 'rgba(34, 197, 94, 0.18)',
  },
  cml: {
    primary: '#f59e0b',
    secondary: '#fbbf24',
    soft: 'rgba(245, 158, 11, 0.12)',
    border: 'rgba(245, 158, 11, 0.32)',
    glow: 'rgba(217, 119, 6, 0.18)',
  },
  rml: {
    primary: '#38bdf8',
    secondary: '#7dd3fc',
    soft: 'rgba(56, 189, 248, 0.12)',
    border: 'rgba(56, 189, 248, 0.32)',
    glow: 'rgba(14, 165, 233, 0.18)',
  },
  pe: {
    primary: '#f472b6',
    secondary: '#f9a8d4',
    soft: 'rgba(244, 114, 182, 0.12)',
    border: 'rgba(244, 114, 182, 0.32)',
    glow: 'rgba(219, 39, 119, 0.18)',
  },
  portco: {
    primary: '#a78bfa',
    secondary: '#c4b5fd',
    soft: 'rgba(167, 139, 250, 0.13)',
    border: 'rgba(167, 139, 250, 0.34)',
    glow: 'rgba(139, 92, 246, 0.2)',
  },
  equity: {
    primary: '#0ea5e9',
    secondary: '#7dd3fc',
    soft: 'rgba(14, 165, 233, 0.12)',
    border: 'rgba(14, 165, 233, 0.34)',
    glow: 'rgba(14, 165, 233, 0.2)',
  },
  repo: {
    primary: '#06b6d4',
    secondary: '#67e8f9',
    soft: 'rgba(6, 182, 212, 0.12)',
    border: 'rgba(6, 182, 212, 0.34)',
    glow: 'rgba(6, 182, 212, 0.18)',
  },
  cash: {
    primary: '#22c55e',
    secondary: '#86efac',
    soft: 'rgba(34, 197, 94, 0.12)',
    border: 'rgba(34, 197, 94, 0.34)',
    glow: 'rgba(34, 197, 94, 0.18)',
  },
  multi: {
    primary: '#38bdf8',
    secondary: '#7dd3fc',
    soft: 'rgba(56, 189, 248, 0.12)',
    border: 'rgba(56, 189, 248, 0.34)',
    glow: 'rgba(14, 165, 233, 0.2)',
  },
};

const tone = (name: StructuredTone) => tones[name];

export const StructuredWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const StructuredHero = styled.section<{ $tone: StructuredTone }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 0.68fr);
  gap: 18px;
  align-items: stretch;
  padding: 18px;
  border-radius: 14px;
  border: 1px solid ${({ $tone }) => tone($tone).border};
  background:
    radial-gradient(circle at 12% 0%, ${({ $tone }) => tone($tone).glow} 0%, rgba(15, 23, 42, 0) 34%),
    linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(2, 6, 23, 0.94) 100%);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.24);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const HeroMain = styled.div`
  display: flex;
  gap: 14px;
  min-width: 0;
`;

export const HeroIcon = styled.div<{ $tone: StructuredTone }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $tone }) => tone($tone).secondary};
  background: ${({ $tone }) => tone($tone).soft};
  border: 1px solid ${({ $tone }) => tone($tone).border};
`;

export const HeroText = styled.div`
  min-width: 0;
`;

export const HeroEyebrow = styled.div<{ $tone: StructuredTone }>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ $tone }) => tone($tone).secondary};
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

export const HeroTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 22px;
  line-height: 1.18;
  letter-spacing: 0;
`;

export const HeroDescription = styled.p`
  margin: 8px 0 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.5;
  max-width: 760px;
`;

export const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 13px;
`;

export const ScopePill = styled.div<{ $tone: StructuredTone }>`
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $tone }) => tone($tone).border};
  background: rgba(15, 23, 42, 0.58);
  color: #cbd5e1;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const HeroSignalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

export const HeroSignal = styled.div<{ $tone: StructuredTone; $accent?: boolean }>`
  min-width: 0;
  padding: 13px;
  border-radius: 12px;
  border: 1px solid ${({ $tone, $accent }) => $accent ? tone($tone).border : 'rgba(71, 85, 105, 0.38)'};
  background: ${({ $tone, $accent }) => $accent ? tone($tone).soft : 'rgba(15, 23, 42, 0.68)'};
`;

export const SignalLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

export const SignalValue = styled.div<{ $tone: StructuredTone; $accent?: boolean }>`
  color: ${({ $tone, $accent }) => $accent ? tone($tone).secondary : '#f8fafc'};
  font-size: 16px;
  font-weight: 900;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

export const StructuredBand = styled.section`
  padding: 18px;
  border-radius: 14px;
  border: 1px solid rgba(51, 65, 85, 0.74);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.78) 0%, rgba(8, 13, 23, 0.9) 100%);
  min-width: 0;

  input,
  select,
  textarea {
    width: 100%;
    box-sizing: border-box;
  }
`;

export const BandHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 760px) {
    flex-direction: column;
  }
`;

export const BandTitleGroup = styled.div`
  min-width: 0;
`;

export const BandEyebrow = styled.div<{ $tone: StructuredTone }>`
  display: flex;
  align-items: center;
  gap: 7px;
  color: ${({ $tone }) => tone($tone).secondary};
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

export const BandTitle = styled.h3`
  margin: 0;
  color: #f8fafc;
  font-size: 17px;
  line-height: 1.24;
  letter-spacing: 0;
`;

export const BandSubline = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.45;
`;

export const InstrumentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 0.82fr) minmax(0, 1.18fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
  }
`;

export const SearchDock = styled.div<{ $tone: StructuredTone }>`
  min-width: 0;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ $tone }) => tone($tone).border};
  background: linear-gradient(180deg, ${({ $tone }) => tone($tone).soft} 0%, rgba(2, 6, 23, 0.22) 100%);

  input {
    width: 100%;
    box-sizing: border-box;
  }
`;

export const DockTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 850;
  margin-bottom: 12px;
`;

export const SnapshotPanel = styled.div`
  min-width: 0;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(71, 85, 105, 0.42);
  background: rgba(2, 6, 23, 0.2);
`;

export const SnapshotHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

export const SnapshotTitleGroup = styled.div`
  min-width: 0;
`;

export const SnapshotTitle = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

export const SnapshotSubline = styled.div`
  color: #94a3b8;
  font-size: 12px;
  margin-top: 4px;
`;

export const SnapshotIcon = styled.div<{ $tone: StructuredTone }>`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $tone }) => tone($tone).secondary};
  background: ${({ $tone }) => tone($tone).soft};
  border: 1px solid ${({ $tone }) => tone($tone).border};
`;

export const SnapshotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 10px;
`;

export const SnapshotMetric = styled.div<{ $tone: StructuredTone; $accent?: boolean }>`
  min-width: 0;
  padding: 11px;
  border-radius: 10px;
  border: 1px solid ${({ $tone, $accent }) => $accent ? tone($tone).border : 'rgba(71, 85, 105, 0.34)'};
  background: ${({ $tone, $accent }) => $accent ? tone($tone).soft : 'rgba(15, 23, 42, 0.58)'};
`;

export const MetricLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

export const MetricValue = styled.div<{ $tone: StructuredTone; $accent?: boolean }>`
  color: ${({ $tone, $accent }) => $accent ? tone($tone).secondary : '#e2e8f0'};
  font-size: 14px;
  font-weight: 850;
  line-height: 1.28;
  overflow-wrap: anywhere;
`;

export const EmptySnapshot = styled.div`
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #94a3b8;
  border: 1px dashed rgba(100, 116, 139, 0.36);
  border-radius: 12px;
  padding: 18px;
`;

export const EmptyTitle = styled.div`
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 850;
  margin-top: 8px;
`;

export const EmptyText = styled.div`
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.45;
  margin-top: 5px;
`;

export const TradeEconomicsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(300px, 0.65fr);
  gap: 18px;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

export const EconomicsPanel = styled.aside<{ $tone: StructuredTone }>`
  padding: 18px;
  border-radius: 14px;
  border: 1px solid ${({ $tone }) => tone($tone).border};
  background:
    radial-gradient(circle at 88% 0%, ${({ $tone }) => tone($tone).glow} 0%, rgba(15, 23, 42, 0) 35%),
    linear-gradient(180deg, rgba(15, 23, 42, 0.82) 0%, rgba(2, 6, 23, 0.92) 100%);
  min-width: 0;
`;

export const EconomicsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
  margin-bottom: 14px;
`;

export const EconomicsIcon = styled.div<{ $tone: StructuredTone }>`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $tone }) => tone($tone).secondary};
  background: ${({ $tone }) => tone($tone).soft};
  border: 1px solid ${({ $tone }) => tone($tone).border};
`;

export const EconomicsMetricGrid = styled.div`
  display: grid;
  gap: 10px;
`;

export const EconomicsMetric = styled.div<{ $tone: StructuredTone; $accent?: boolean }>`
  padding: 13px;
  border-radius: 11px;
  border: 1px solid ${({ $tone, $accent }) => $accent ? tone($tone).border : 'rgba(71, 85, 105, 0.36)'};
  background: ${({ $tone, $accent }) => $accent ? tone($tone).soft : 'rgba(15, 23, 42, 0.6)'};
`;

export const formatCurrencyValue = (value: number, currency = 'USD') => {
  if (!Number.isFinite(value) || value <= 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatNumberValue = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '-';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
};

export const StructuredHeroPanel: React.FC<{
  toneName: StructuredTone;
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  description: string;
  badges: string[];
  signals: StructuredMetric[];
}> = ({ toneName, icon: Icon, eyebrow, title, description, badges, signals }) => (
  <StructuredHero $tone={toneName}>
    <HeroMain>
      <HeroIcon $tone={toneName}><Icon size={22} /></HeroIcon>
      <HeroText>
        <HeroEyebrow $tone={toneName}>{eyebrow}</HeroEyebrow>
        <HeroTitle>{title}</HeroTitle>
        <HeroDescription>{description}</HeroDescription>
        <PillRow>
          {badges.map((badge) => (
            <ScopePill key={badge} $tone={toneName}>{badge}</ScopePill>
          ))}
        </PillRow>
      </HeroText>
    </HeroMain>
    <HeroSignalGrid>
      {signals.map((metric) => (
        <HeroSignal key={metric.label} $tone={toneName} $accent={metric.accent}>
          <SignalLabel>{metric.label}</SignalLabel>
          <SignalValue $tone={toneName} $accent={metric.accent}>{metric.value}</SignalValue>
        </HeroSignal>
      ))}
    </HeroSignalGrid>
  </StructuredHero>
);

export const StructuredContextPanel: React.FC<{
  toneName: StructuredTone;
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  rows: StructuredContextRow[];
  emptyTitle: string;
  emptyText: string;
}> = ({ toneName, icon: Icon, title, subtitle, rows, emptyTitle, emptyText }) => (
  <SnapshotPanel>
    <SnapshotHeader>
      <SnapshotTitleGroup>
        <SnapshotTitle>{title}</SnapshotTitle>
        {subtitle && <SnapshotSubline>{subtitle}</SnapshotSubline>}
      </SnapshotTitleGroup>
      <SnapshotIcon $tone={toneName}><Icon size={18} /></SnapshotIcon>
    </SnapshotHeader>
    {rows.length > 0 ? (
      <SnapshotGrid>
        {rows.map((row) => (
          <SnapshotMetric key={row.label} $tone={toneName} $accent={row.accent}>
            <MetricLabel>{row.label}</MetricLabel>
            <MetricValue $tone={toneName} $accent={row.accent}>{row.value}</MetricValue>
          </SnapshotMetric>
        ))}
      </SnapshotGrid>
    ) : (
      <EmptySnapshot>
        <div>
          <AlertCircle size={20} />
          <EmptyTitle>{emptyTitle}</EmptyTitle>
          <EmptyText>{emptyText}</EmptyText>
        </div>
      </EmptySnapshot>
    )}
  </SnapshotPanel>
);

export const EconomicsPreview: React.FC<{
  toneName: StructuredTone;
  title: string;
  notionalLabel: string;
  notionalValue: number;
  price: number;
  priceLabel?: string;
  priceValue?: React.ReactNode;
  consideration: number;
  currency?: string;
  extraMetrics?: StructuredMetric[];
}> = ({ toneName, title, notionalLabel, notionalValue, price, priceLabel = 'Price', priceValue, consideration, currency = 'USD', extraMetrics = [] }) => (
  <EconomicsPanel $tone={toneName}>
    <EconomicsHeader>
      <EconomicsIcon $tone={toneName}><Calculator size={17} /></EconomicsIcon>
      {title}
    </EconomicsHeader>
    <EconomicsMetricGrid>
      <EconomicsMetric $tone={toneName}>
        <MetricLabel>{notionalLabel}</MetricLabel>
        <MetricValue $tone={toneName}>{formatNumberValue(notionalValue)}</MetricValue>
      </EconomicsMetric>
      <EconomicsMetric $tone={toneName}>
        <MetricLabel>{priceLabel}</MetricLabel>
        <MetricValue $tone={toneName}>{priceValue ?? (Number.isFinite(price) && price > 0 ? `${price.toFixed(2)}%` : '-')}</MetricValue>
      </EconomicsMetric>
      <EconomicsMetric $tone={toneName} $accent>
        <MetricLabel>Consideration</MetricLabel>
        <MetricValue $tone={toneName} $accent>{formatCurrencyValue(consideration, currency)}</MetricValue>
      </EconomicsMetric>
      {extraMetrics.map((metric) => (
        <EconomicsMetric key={metric.label} $tone={toneName} $accent={metric.accent}>
          <MetricLabel>{metric.label}</MetricLabel>
          <MetricValue $tone={toneName} $accent={metric.accent}>{metric.value}</MetricValue>
        </EconomicsMetric>
      ))}
    </EconomicsMetricGrid>
  </EconomicsPanel>
);
